"use client";

import {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAnalytics } from "@/lib/analytics/hooks";
import { trackMonetizationEvent } from "@/lib/analytics/track";
import type { Locale } from "@/lib/i18n/config";
import { wasSlotOutcomeRequested } from "@/lib/monetization/accounting-identity";
import { appendAdContextParams } from "@/lib/monetization/ad-context-client";
import { CONTEXT_BANDIT_FLAG } from "@/lib/monetization/context-bandit";
import type { NormalizedOffer } from "@/lib/monetization/providers";
import { useAdGuardrails } from "@/lib/monetization/use-ad-guardrails";
import { useSlotRequestTerminal } from "@/lib/monetization/use-slot-request-terminal";
import { HouseSlotCard } from "./house-slot-card";
import { OfferCard } from "./offer-card";

/**
 * Native, provider-agnostic ad unit. Fetches the engine-chosen offer for a
 * surface (force-dynamic `/api/monetization/offer`, geo-IP correct) and renders
 * it through `<OfferCard>` — single-CTA (Wise/Deel/Educative/Contra) or a
 * product grid (Shopee). While loading it reserves space with a skeleton (no
 * CLS).
 *
 * THERE IS NO affiliate `fallback`. It used to render `LegacyAffiliateUnit`
 * whenever the engine was off, nothing was eligible, or the guardrails blocked
 * the slot — which is what kept five duplicate card components alive. A
 * monetization KILL still renders NOTHING, so `engine.enabled: false` stays a
 * true kill switch rather than a silent hand-off to a second, unlabelled,
 * untracked card. Per-surface kills belong in `settings.surfaces.*.enabled`,
 * not the master switch.
 *
 * A FREQUENCY CAP IS NOT A KILL, and conflating the two cost us the slot. When
 * the guardrail says `affiliate_cap`/`session_cap` the viewer is fine — we just
 * decided not to sell them a third thing this session — but the slot still
 * rendered an empty box holding 152px of reserved height. Over the 7 days to
 * 2026-09-07 that was 91% of every `feed_inline` slot (29 shown / 292
 * suppressed) and 97% of `category_footer`: a band of dead whitespace mid-feed,
 * for 100 people, invisible to every alarm we have. Those two reasons now fall
 * back to `HouseSlotCard`, a first-party unit — the same answer `AdSlot`
 * already reached for its own no-fill hole. `pro`, `dismissed` and `empty`
 * still render nothing, and now collapse rather than hold the height: there is
 * no offer coming, so reserving space for one just moves the hole.
 *
 * Decision (which provider) is the engine's; format (how it looks) is here.
 * Both AffiliateImpression (viewability + shared click_id) and AffiliateLink
 * (rel=sponsored + click) tracking carry through automatically.
 */

type State =
	| { status: "idle" }
	| { status: "loading" }
	| { status: "offer"; offer: NormalizedOffer; market?: string }
	| { status: "empty" };

// Minimum footprint shared by every render state that still HAS something to
// show — the skeleton, a resolved offer, and the first-party house card — so
// switching between them never shifts the feed content below. This is the
// concrete mechanism behind the "ad slot" region flagged in the mobile CLS
// audit (P1-5, docs/plans/backlog/product-signal-audit-remediation-2026-08-04
// .md): with no shared minHeight the skeleton collapses to a 0px div
// the instant an offer resolves, and the feed rows below it jump.
//
// It is NOT applied to the terminal no-offer states (`empty`, `pro`,
// `dismissed`). Reserving space there does not prevent a shift, it guarantees
// a permanent one-slot hole — which is exactly what the feed was showing for
// 91% of its `feed_inline` slots. Those states collapse instead; the guardrail
// resolves within a second or two of load and these slots sit below the fold,
// so the collapse is off-screen and outside the CLS window. (A user-initiated
// dismiss was already exempt on its own grounds: Chrome's Layout Instability
// API excludes shifts within 500ms of user input.)
//
// 232px, not the original 152px: a text-only OfferCard (accent bar, eyebrow,
// title, three-line description, CTA, disclosure row) renders at ~229px, so a
// 152px reserve still let the feed jump ~77px when the offer resolved on
// screen. That was the top layout-shift source on /id/jobs (CrUX CLS p75 0.26,
// 2026-10-08; lab trace: `feed_inline` slot growing 152px -> 229px).
const RESERVED_MIN_HEIGHT_CLASS = "min-h-[232px]";

function reservedClassName(
	className: string | undefined,
	minHeightClass: string = RESERVED_MIN_HEIGHT_CLASS,
): string {
	return className ? `${className} ${minHeightClass}` : minHeightClass;
}

export function NativeAd({
	surface,
	categoryId = null,
	jobId = null,
	locale,
	className,
	anchor,
	positionIndex,
	emptyFallback,
	minHeightClass,
	onSettled,
	capExempt = false,
}: {
	surface: string;
	categoryId?: string | null;
	jobId?: string | null;
	locale: Locale;
	className?: string;
	/** Placement anchor id this instance rendered at (PR-5) — carried into the
	 * `affiliate_impression`/`affiliate_click` `extra` payload so per-anchor
	 * exposure→conversion can be queried in PostHog. Omit for a surface with
	 * no registered anchors. */
	anchor?: string;
	/** Payload-tunable list-position index (e.g. feed row), carried into the
	 * same `extra` payload as `position_index`. Omit for a non-list surface. */
	positionIndex?: number;
	/**
	 * Rendered in place (at the reserved height) when the engine returns no
	 * offer, instead of collapsing to nothing — the "house card only if the
	 * engine is empty" rung of the display-slot fallback ladder. Adds no event:
	 * the `affiliate_slot_empty` terminal already fired for this request.
	 * Pro / dismissed / kill states still collapse.
	 */
	emptyFallback?: ReactNode;
	/** Static `min-h-[Npx]` class replacing the default 232px reserved height,
	 * so a unit standing in for a taller slot holds that slot's height. */
	minHeightClass?: string;
	/** Called once, when the unit stops showing its loading skeleton. */
	onSettled?: () => void;
	/** Skip the session frequency caps (Pro still blocks) — for a caller whose
	 * frequency is governed elsewhere, i.e. the ad wall. */
	capExempt?: boolean;
}) {
	// Starts "idle" (skeleton, no network) and only fetches the offer when the
	// slot nears the viewport. The old version fetched on mount for EVERY slot on
	// EVERY page view — so below-the-fold inline slots (feed rows 5 & 14) ran a
	// no-store worker round-trip that often painted only after the user had
	// already scrolled past, and slots the user never reached still burned a
	// fetch (wasted Workers CPU + noisy bandit data). Lazy-loading with a lead
	// rootMargin starts the draw just before the slot is seen, so the card is
	// ready on arrival and we only spend a fetch on impressions that can actually
	// be viewed. Rotation is untouched: each slot still draws its own fresh
	// Thompson sample via the per-mount nonce + the route's no-store header.
	const [state, setState] = useState<State>({ status: "idle" });
	const [dismissed, setDismissed] = useState(false);
	const ranRef = useRef(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const { posthog } = useAnalytics();

	// "Aggressive but not annoying" gate — Pro-respect + per-session frequency caps
	// + dismiss, enforced client-side where the session state lives. Suppressions
	// are reported to telemetry so the caps can be tuned. Until `ready`, we hold the
	// skeleton and fetch NOTHING (avoids showing an ad to a Pro member for a frame,
	// and avoids a wasted offer fetch for a capped/blocked viewer).
	const guard = useAdGuardrails(surface, "affiliate", { capExempt });
	// Ref, not a dep: a late segment resolve must not re-arm the observer.
	const segmentRef = useRef(guard.segment);
	segmentRef.current = guard.segment;
	// Report EVERY terminal non-offer outcome, once per mount. `blocked` has
	// always been reported; `empty` — the engine returning no offer, i.e. the
	// master switch off, no eligible arm, or a failed fetch — was completely
	// silent in PostHog, in D1 and in the server logs, so a total monetization
	// blackout looked exactly like a healthy page with blank divs on it. With
	// both reported and `affiliate_impression` counting the fills, a per-surface
	// fill rate is computable for the first time.
	//
	// A provider dismissed in a PRIOR mount is included in the ladder (PR 1,
	// zero-blindspot accounting): `guard.isDismissed` short-circuits the render
	// at line ~295 BEFORE `OfferCard`/`AffiliateImpression` ever mounts, so this
	// mount's `requested` (fired in `loadOffer` below) previously had no
	// terminal event at all — a real gap, and safe to add here because no
	// impression can have fired for it. Deliberately NOT the local `dismissed`
	// state below: by the time a viewer dismisses THIS mount's own card, an
	// impression has already fired for it (they had to see it to click dismiss),
	// so adding it here would double-count one `requested` into both
	// `impressions` and `suppressed` and break the identity in the other
	// direction. That in-mount dismiss stays covered by its own
	// `monetized_dismiss` event only, same as before this PR.
	const suppressedRef = useRef(false);
	const {
		begin,
		settle,
		awaitImpression,
		confirmImpression,
		markTerminal,
		canImpress,
	} = useSlotRequestTerminal(ranRef, surface, locale);
	// With `adsDismissible: false` for this segment there is no close control,
	// and a dismissal stored earlier in the session no longer hides the card.
	const providerDismissed =
		state.status === "offer" &&
		guard.dismissible &&
		guard.isDismissed(state.offer.provider);
	const suppressionReason = !guard.ready
		? null
		: guard.blocked
			? (guard.blockReason ?? "blocked")
			: state.status === "empty"
				? "empty"
				: providerDismissed
					? "dismissed"
					: null;
	useEffect(() => {
		if (!suppressionReason || suppressedRef.current) return;
		suppressedRef.current = true;
		// PostHog is unaffected by the gate below — it never had a "requested"
		// counterpart to reconcile against, so `blocked` (pro/cap) has always
		// been reported here regardless of whether a fetch ever ran.
		posthog?.capture("monetized_suppressed", {
			surface,
			reason: suppressionReason,
		});
		// D1 must NOT emit a terminal outcome for a request that never
		// happened — CAUGHT IN REVIEW: `guard.blocked` (pro/cap) can resolve
		// true BEFORE `loadOffer` ever runs (the load effect below early-
		// returns on `guard.ready && guard.blocked`, so `ranRef` stays false),
		// which previously fired `affiliate_slot_suppressed` with ZERO matching
		// `affiliate_slot_requested` — deterministically, on every single
		// pageview by a Pro member. That breaks the exact identity this file
		// exists to satisfy, and would have paged the new p0 Sentry alarm from
		// ordinary Pro traffic on day one.
		//
		// wasSlotOutcomeRequested is the single gate this rule lives in — see
		// its doc comment in accounting-identity.ts for the full incident this
		// fixes. `ranRef.current` is the same idempotence ref `loadOffer` itself
		// uses, so it is the exact source of truth for "did a request fire".
		if (!wasSlotOutcomeRequested(suppressionReason, ranRef.current)) return;
		trackMonetizationEvent({
			eventKind:
				suppressionReason === "empty"
					? "affiliate_slot_empty"
					: "affiliate_slot_suppressed",
			surface,
			locale,
		});
		// This request now has its terminal event; an unmount must not add another.
		markTerminal();
	}, [suppressionReason, surface, locale, posthog, markTerminal]);

	// Zero-blindspot accounting, incident 2 (2026-09-23): a resolved offer
	// with `suppressionReason === null` is exactly the offer this component
	// is about to hand to `OfferCard` — the ladder above already accounts for
	// every OTHER case (blocked/dismissed/empty). Arm the pending-impression
	// flag here so an unmount/`pagehide` before the 50%-visibility observer
	// fires still reaches a terminal (`affiliate_slot_empty`, `slot:
	// "unseen"`) instead of the request going permanently unaccounted — see
	// `useSlotRequestTerminal`'s doc comment for the full incident.
	const impressionPending =
		guard.ready && state.status === "offer" && !suppressionReason;
	useEffect(() => {
		if (impressionPending) awaitImpression();
	}, [impressionPending, awaitImpression]);

	const loadOffer = useCallback(() => {
		if (ranRef.current) return;
		ranRef.current = true;
		setState({ status: "loading" });
		// Zero-blindspot accounting (PR 1): every resolution ATTEMPT, fired from
		// this single choke point — it is the only place all three trigger paths
		// (the IntersectionObserver below, and the two immediate-call fallbacks
		// for no-IO / no-element) converge, and `ranRef` above is what keeps it
		// to exactly one emit per mount even under React strict mode's
		// double-invoke.
		trackMonetizationEvent({
			eventKind: "affiliate_slot_requested",
			surface,
			locale,
		});
		const params = new URLSearchParams({ surface, locale });
		if (categoryId) params.set("category", categoryId);
		if (jobId) params.set("jobId", jobId);
		// Per-mount nonce: distinct URL per ad slot + per navigation so the browser
		// can't dedupe/cache two slots into one offer, and each draws an independent
		// Thompson sample. Paired with the route's no-store header.
		params.set("n", `${Date.now()}-${Math.floor(Math.random() * 1e9)}`);
		// Wave 4c targeting context + fatigue ledger (ignored server-side while
		// `engine.targeting.enabled` is false). Segment is absent when the draw
		// starts before the guardrail resolves — the server reads it as unknown.
		appendAdContextParams(params, {
			segment: segmentRef.current,
			// 50/50 A/B (contextual bandit vs current engine). Read at fetch
			// time; an unresolved flag sends nothing and the route serves control.
			banditArm: posthog?.getFeatureFlag?.(CONTEXT_BANDIT_FLAG),
		});
		const signal = begin();
		fetch(`/api/monetization/offer?${params.toString()}`, {
			cache: "no-store",
			signal,
		})
			.then((r) => (r.ok ? r.json() : null))
			.then((data) => {
				// Aborted = unmounted / page hidden mid-fetch: the terminal event
				// was already emitted by useSlotRequestTerminal; never setState.
				if (!settle(signal)) return;
				const parsed = data as {
					offer?: NormalizedOffer | null;
					market?: string;
				} | null;
				const offer = parsed?.offer;
				setState(
					offer
						? { status: "offer", offer, market: parsed?.market }
						: { status: "empty" },
				);
			})
			.catch(() => {
				if (!settle(signal)) return;
				setState({ status: "empty" });
			});
	}, [surface, categoryId, jobId, locale, begin, settle, posthog]);

	useEffect(() => {
		if (ranRef.current) return;
		// Never fetch for a viewer already known to be blocked (Pro / session
		// cap) — a blocked unit renders an empty reserved box. But do NOT wait
		// for the guardrail decision before watching the viewport (2026-09-05):
		// that decision sits behind the better-auth session round-trip, 1–5 s
		// on this app, and the observer's 600 px head start only began counting
		// once it returned — by which point the reader was already past the
		// slot. The draw now starts as soon as the slot nears the viewport and
		// runs in PARALLEL with the guardrail; the render below still holds the
		// skeleton until `guard.ready`, and a viewer who resolves to blocked
		// after the fetch simply never sees the offer it fetched. One wasted
		// request for a Pro member beats a missed impression for everyone else.
		if (guard.ready && guard.blocked) return;
		// No IntersectionObserver (SSR / jsdom / very old browsers) → load now so
		// the unit never silently stays blank.
		if (typeof IntersectionObserver === "undefined") {
			loadOffer();
			return;
		}
		const el = containerRef.current;
		if (!el) {
			loadOffer();
			return;
		}
		const io = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					io.disconnect();
					loadOffer();
				}
			},
			// Start the draw ~one viewport early so the round-trip finishes before
			// the slot is actually on screen.
			{ rootMargin: "600px 0px" },
		);
		io.observe(el);
		return () => io.disconnect();
	}, [loadOffer, guard.ready, guard.blocked]);

	// Tell the parent once the skeleton is gone (offer, house card, or a
	// collapsed state). The ad wall starts its countdown here, so the visitor
	// is never let through before the sponsor has painted.
	const settled =
		dismissed ||
		(guard.ready &&
			(guard.blocked || state.status === "empty" || state.status === "offer"));
	const settledRef = useRef(false);
	useEffect(() => {
		if (!settled || settledRef.current) return;
		settledRef.current = true;
		onSettled?.();
	}, [settled, onSettled]);

	// The session budget is charged from `OfferCard`'s own 50%-visibility
	// observer (`onImpression` below), NOT from the offer resolving. Resolving
	// happens a full viewport early — `rootMargin: "600px 0px"` — so charging
	// there spent the cap on units the reader never reached, and on the layout's
	// `md:hidden` sticky bar which on desktop is `display:none` and can never be
	// seen at all. That is what starved the in-content slots: `mobile_sticky`
	// took 524 of the 752 affiliate impressions in the week to 2026-09-07 while
	// `feed_inline` got 29. Idempotent per mount via the hook's own ref.

	// Blocked or dismissed → never an affiliate ad and never a stuck skeleton.
	// What takes its place depends on WHY:
	//
	//  - `affiliate_cap` / `session_cap` — a frequency decision about this
	//    session, not about monetization. Fall back to the first-party
	//    `HouseSlotCard` and keep the reserved height, because something is
	//    genuinely rendering there.
	//  - `pro` — the member paid for an ad-free page. Re-filling the space with
	//    a house pitch is the thing they paid to stop. Collapse.
	//  - `dismissed` — the viewer asked for it gone. Collapse (and it is exempt
	//    from the hold anyway: Chrome excludes shifts within 500ms of input).
	//  - anything else — collapse rather than hold a hole.
	if (dismissed || (guard.ready && guard.blocked)) {
		const capped =
			!dismissed &&
			(guard.blockReason === "affiliate_cap" ||
				guard.blockReason === "session_cap");
		if (capped)
			return (
				<div
					className={reservedClassName(className, minHeightClass)}
					data-testid="native-ad"
					data-state="house"
				>
					<HouseSlotCard
						surface={surface}
						locale={locale}
						topicSlug={categoryId ?? undefined}
					/>
				</div>
			);
		return (
			<div
				className={className}
				data-testid="native-ad"
				data-state={dismissed ? "dismissed" : "blocked"}
			/>
		);
	}

	// Skeleton while the offer is still in flight OR while the guardrail has
	// not decided yet — an offer already in hand must not paint before Pro /
	// session-cap status is known (see the observer effect above).
	if (!guard.ready || state.status === "idle" || state.status === "loading") {
		return (
			<div
				className={reservedClassName(className, minHeightClass)}
				aria-hidden="true"
				ref={containerRef}
				data-testid="native-ad"
				data-state={guard.ready ? state.status : "guard-pending"}
			>
				{/* Shape must track OfferCard exactly — a radius change here without one
					    there pops the card into a different silhouette the moment the
					    offer resolves. */}
				<div className="overflow-hidden rounded-xl border border-border/60 bg-card">
					<div className="h-1 w-full bg-muted" />
					<div className="space-y-3 p-5">
						<Skeleton className="h-3 w-24" />
						<Skeleton className="h-4 w-40" />
						<Skeleton className="h-3 w-full" />
						<Skeleton className="h-3 w-full" />
						<Skeleton className="h-3 w-2/3" />
						<Skeleton className="h-9 w-32 rounded-full" />
						<Skeleton className="h-3 w-20" />
					</div>
				</div>
			</div>
		);
	}

	// A kill, not a cap: no offer is coming, so holding 232px reserves space for
	// something that will never arrive. Collapse. The guard resolves within a
	// second or two of load and these slots sit far below the fold, so the
	// collapse is off-screen and outside the CLS window.
	if (state.status === "empty")
		return emptyFallback ? (
			<div
				className={reservedClassName(className, minHeightClass)}
				data-testid="native-ad"
				data-state="empty-fallback"
			>
				{emptyFallback}
			</div>
		) : (
			<div className={className} data-testid="native-ad" data-state="empty" />
		);

	// A provider the viewer already rejected this session → don't show it again
	// (only while the payload still lets this segment close ads).
	if (providerDismissed)
		return (
			<div
				className={className}
				data-testid="native-ad"
				data-state="dismissed"
			/>
		);

	return (
		<OfferCard
			offer={state.offer}
			market={state.market}
			surface={surface}
			jobId={jobId}
			locale={locale}
			className={reservedClassName(className, minHeightClass)}
			anchor={anchor}
			positionIndex={positionIndex}
			testId="native-ad"
			canFire={canImpress}
			onImpression={() => {
				guard.recordImpression();
				confirmImpression();
			}}
			onDismiss={
				guard.dismissible
					? () => {
							guard.dismiss(state.offer.provider);
							posthog?.capture("monetized_dismiss", {
								surface,
								platform: state.offer.provider,
								locale,
							});
							setDismissed(true);
						}
					: undefined
			}
		/>
	);
}
